import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { MQS, SIZES } from '../app/common/constants/styles';
import Header from './Header';
import { SigninButton, SignupButton } from './components';
import FooterContainer from '../footer/FooterContainer';
import { TranslationHelpers } from '../i18n/translationHelpers';
import ServicesContent from './components/ServicesContent';
import { IReduxRootState } from '../app/reduxRootState.interface';

const Content = styled.div`
    max-width: ${1 * SIZES.appMaxWidth}px;
    margin: 0px auto;

    @media (max-width: ${MQS.mq2Min}) {
        padding: 20px;
    }

    @media (min-width: ${MQS.mq2Min}) and (max-width: ${MQS.mq3Min}) {
        padding: 20px;
    }

    @media (min-width: ${MQS.mq3Min}) and (max-width: ${MQS.mq3Max}) {
        padding: 20px;
    }
`;

const MainTitle = styled.h1`
    font-size: 2.25em;
    font-weight: bold;
    font-family: 'Rakuten Sans UI Bold', sans-serif;
    letter-spacing: 2px;
    text-align: center;
    margin: 40px auto;

    @media (max-width: ${MQS.mq2Min}) {
        padding: 20px 50px;
    }
`;

const InfoContainer = styled.div`
    background-color: #d8d8d8;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.12);
    width: 100%;
    padding: 0px 20px;
`;

const InfoContent = styled.div`
    margin: 0px auto;
    padding: 20px 0px;
    max-width: ${1 * SIZES.appMaxWidth}px;
    display: flex;

    @media (max-width: ${MQS.mq2Min}) {
        flex-wrap: wrap;
        padding: 20px 50px;
    }
`;

const InfoContentColumn = styled.div`
    display: flex;
    flex-direction: column;

    h4 {
        font-size: 1.125em;
        margin-top: 10px;
    }

    span {
        font-size: 0.875em;
        padding-bottom: 10px;
        line-height: 18px;
    }
`;

const VerticalLine = styled.div`
    margin: 0px 2.1875em;
    border: 0.5px solid #979797;
    @media (max-width: ${MQS.mq2Min}) {
        width: 100%;
        margin: 20px 0px;
    }
`;

const SignInContent = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin: 25px 0px;

    h2 {
        font-family: Rakuten Sans UI Semi-Bold, sans-serif;
    }

    button {
        margin: 20px 0px;
    }
`;

const About = () => {
    const appConfig = useSelector((state: IReduxRootState) => state.app.appConfig);

    return (
        // 👇 Setting "lang" as otherwise the default font family isn't set (depends on this attribute)
        //    Lang attribute is usually set at root <html> tag but can be missing if entering the site without any client id configured.
        //    Font family could also be specified with CSS to this <div>, but then buttons and title would be incorrect too
        <div lang="auto">
            <Header appConfig={appConfig} />
            <MainTitle>
                {TranslationHelpers.getFormattedMessage('visitor.main.heading.1')} <br />
                {TranslationHelpers.getFormattedMessage('visitor.main.heading.2')}
            </MainTitle>
            <InfoContainer>
                <InfoContent>
                    <InfoContentColumn>
                        <h4>{TranslationHelpers.getFormattedMessage('visitor.info.heading.1')}</h4>
                        <span>{TranslationHelpers.getFormattedMessage('visitor.info.body.1')}</span>
                    </InfoContentColumn>
                    <VerticalLine />
                    <InfoContentColumn>
                        <h4>{TranslationHelpers.getFormattedMessage('visitor.info.heading.2')}</h4>
                        <span>{TranslationHelpers.getFormattedMessage('visitor.info.body.2')}</span>
                    </InfoContentColumn>
                    <VerticalLine />
                    <InfoContentColumn>
                        <h4>{TranslationHelpers.getFormattedMessage('visitor.info.heading.3')}</h4>
                        <span>{TranslationHelpers.getFormattedMessage('visitor.info.body.3')}</span>
                    </InfoContentColumn>
                </InfoContent>
            </InfoContainer>
            <Content data-qa-id="rakuten-main-content">
                <ServicesContent />
                <SignInContent>
                    <h2>{TranslationHelpers.getFormattedMessage('visitor.get.started')}</h2>
                    <SignupButton appConfig={appConfig} />
                    <SigninButton appConfig={appConfig} />
                </SignInContent>
            </Content>
            <FooterContainer />
        </div>
    );
};

export default About;
